import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { flushSync } from "react-dom";
import { useCreateSessionMutation, useResolveInstanceMutation } from "@/store/api/sessionApi";
import { saveInstances } from "@/store/slices/instancesSlice";
import { saveTenantsList } from "@/store/slices/tenantsSlice";
import {
  setPermissions,
  setCapabilities,
  setIsAuthenticated,
  setInitialized,
  setIsFullAccess,
  setInstanceCultureName,
  setInstanceTimezone,
  setAccessToken,
} from "@/store/slices/authSlice";
import { authService, getAccessToken as acquireMsalToken } from "@/utils/authService";
import type {
  SessionResponse,
  ResolveInstanceResponse,
  AccessibleInstance,
} from "../../shared/types/rbac";

export type SessionState = "loading" | "blocked" | "no-access" | "error" | "ready";

interface UseSessionInitReturn {
  sessionState: SessionState;
  retry: () => void;
  isLoaded: boolean;
}

export function useSessionInit(): UseSessionInitReturn {
  const dispatch = useDispatch();
  const [sessionState, setSessionState] = useState<SessionState>("loading");
  const [isLoaded, setIsLoaded] = useState(false);
  const [createSession] = useCreateSessionMutation();
  const [resolveInstance] = useResolveInstanceMutation();

  const populateReduxFromSession = useCallback(
    (session: SessionResponse, resolved: ResolveInstanceResponse) => {
      // Populate instances slice
      const instances = session.accessibleInstances.map((inst: AccessibleInstance) => ({
        id: inst.instanceId,
        name: inst.instanceName || inst.operatorName || inst.instanceId,
        displayname: inst.instanceName || inst.operatorName || inst.instanceId,
      }));

      dispatch(
        saveInstances({
          instances,
          selectedInstanceName:
            resolved.instance.instanceName || resolved.instance.operatorName || "",
          selectedInstanceId: resolved.instance.instanceId,
          tenant: {
            id: resolved.instance.tenantId || "local",
            businessname:
              session.accessibleInstances.find(
                (i: AccessibleInstance) => i.instanceId === resolved.instance.instanceId,
              )?.tenantName || "SkyAdvisor",
          },
        }),
      );

      // Populate tenants slice — group by tenant
      // ITenant has many legacy required fields; cast since only id/businessname/instances are used
      const tenantMap = new Map<string, Record<string, unknown>>();
      for (const inst of session.accessibleInstances) {
        const tid = inst.tenantId || "local";
        const tname = inst.tenantName || "SkyAdvisor";
        if (!tenantMap.has(tid)) {
          tenantMap.set(tid, { id: tid, name: tname, businessname: tname, instances: [] });
        }
        (tenantMap.get(tid)!.instances as Array<Record<string, unknown>>).push({
          id: inst.instanceId,
          name: inst.instanceName || inst.operatorName || inst.instanceId,
          displayname: inst.instanceName || inst.operatorName || inst.instanceId,
        });
      }

      dispatch(
        saveTenantsList({
          tenants: Array.from(tenantMap.values()) as any,
          selectedTenant: {
            id: resolved.instance.tenantId || "local",
            name:
              session.accessibleInstances.find(
                (i: AccessibleInstance) => i.instanceId === resolved.instance.instanceId,
              )?.tenantName || "SkyAdvisor",
          },
        }),
      );

      // Populate auth slice — map flags to existing Redux types
      dispatch(
        setPermissions({
          isPlatformAdmin: resolved.flags.isSkyPointAdmin,
          isTenantAdmin: resolved.flags.isAdmin,
          isInstanceAdmin: resolved.flags.isAdmin,
        }),
      );
      dispatch(
        setCapabilities({
          allowView: resolved.flags.allowView,
          allowEdit: resolved.flags.allowEdit,
          allowDelete: resolved.flags.allowDelete,
        }),
      );
      dispatch(setIsFullAccess(true));
      dispatch(setIsAuthenticated(true));
      dispatch(setInitialized(true));
      dispatch(setInstanceCultureName("en-US"));
      dispatch(setInstanceTimezone("US/Pacific"));
    },
    [dispatch],
  );

  const initSession = useCallback(async () => {
    setSessionState("loading");

    try {
      // Acquire MSAL token and put it in Redux so baseApi can use it
      const token = await acquireMsalToken();
      dispatch(setAccessToken(token));

      const sessionResult = await createSession().unwrap();

      if (sessionResult.accessibleInstances.length === 0) {
        setSessionState("no-access");
        return;
      }

      // Determine target instance
      const urlParams = new URLSearchParams(window.location.search);
      const urlInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
      const savedInstanceId = authService.getSavedInstanceId();
      const targetInstanceId =
        urlInstanceId || savedInstanceId || sessionResult.accessibleInstances[0].instanceId;

      // Resolve the target instance
      let resolveResult: ResolveInstanceResponse;
      try {
        resolveResult = await resolveInstance({ instanceId: targetInstanceId }).unwrap();
      } catch {
        resolveResult = await resolveInstance({
          instanceId: sessionResult.accessibleInstances[0].instanceId,
        }).unwrap();
      }

      if (!resolveResult.hasAccess) {
        const fallbackId = sessionResult.accessibleInstances[0].instanceId;
        resolveResult = await resolveInstance({ instanceId: fallbackId }).unwrap();
      }

      populateReduxFromSession(sessionResult, resolveResult);

      // Update URL if needed
      const resolvedId = resolveResult.instance.instanceId;
      if (resolvedId) {
        const currentUrlParams = new URLSearchParams(window.location.search);
        const currentUrlInstanceId =
          currentUrlParams.get("instanceid") || currentUrlParams.get("instanceId");
        if (currentUrlInstanceId !== resolvedId) {
          currentUrlParams.delete("instanceid");
          currentUrlParams.delete("instanceId");
          currentUrlParams.set("instanceid", resolvedId);
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}?${currentUrlParams.toString()}`,
          );
        }
        authService.saveInstanceId(resolvedId);
      }

      flushSync(() => {
        setSessionState("ready");
        setIsLoaded(true);
      });
    } catch (err: unknown) {
      const error = err as { status?: number; data?: { code?: string } };
      if (error?.status === 403 && error?.data?.code === "ACCOUNT_BLOCKED") {
        setSessionState("blocked");
        return;
      }
      setSessionState("error");
    }
  }, [createSession, resolveInstance, populateReduxFromSession]);

  useEffect(() => {
    initSession();
  }, [initSession]);

  return { sessionState, retry: initSession, isLoaded };
}
